'use client';
import React, { useState, useRef, useEffect } from 'react';
import { Bell, Flame, Star, LogOut, User, Settings, CheckCheck } from 'lucide-react';
import { useLanguage } from '@/lib/LanguageContext';
import { useApp } from '@/lib/AppContext';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';

export default function Topbar() {
  const { language, setLanguage } = useLanguage();
  const { user, notifications, unreadCount, markAllRead, markRead, logout } = useApp();
  const router = useRouter();

  const [notifOpen, setNotifOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    toast.success(language === 'fr' ? 'Déconnexion réussie' : 'Выход выполнен');
    router.push('/sign-up-login-screen');
  };

  const handleMarkAllRead = () => {
    markAllRead();
    toast.success(language === 'fr' ? 'Toutes les notifications lues' : 'Все уведомления прочитаны');
  };

  const NOTIF_TYPE_COLORS: Record<string, string> = {
    achievement: 'bg-amber-100 text-amber-700',
    streak: 'bg-orange-100 text-orange-700',
    lesson: 'bg-blue-100 text-blue-700',
    system: 'bg-gray-100 text-gray-600',
  };

  return (
    <header className="h-14 bg-card border-b border-border flex items-center justify-between px-6 lg:px-8 flex-shrink-0 relative z-30">
      {/* Left: breadcrumb placeholder */}
      <div />
      {/* Right: streak + xp + lang + notifications + avatar */}
      <div className="flex items-center gap-3">
        {/* Streak */}
        <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
          <Flame size={14} className="text-accent" />
          <span className="text-sm font-700 text-accent font-tabular">{user?.streak}</span>
        </div>

        {/* XP */}
        <div className="flex items-center gap-1.5 bg-primary/5 border border-primary/20 rounded-full px-3 py-1">
          <Star size={14} className="text-primary" />
          <span className="text-sm font-700 text-primary font-tabular">{user?.xp?.toLocaleString()}</span>
        </div>

        {/* Language toggle */}
        <div className="flex items-center bg-muted rounded-full p-0.5">
          <button
            onClick={() => setLanguage('fr')}
            className={`px-3 py-1 text-xs font-600 rounded-full transition-all duration-150 ${
              language === 'fr' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            FR
          </button>
          <button
            onClick={() => setLanguage('ru')}
            className={`px-3 py-1 text-xs font-600 rounded-full transition-all duration-150 ${
              language === 'ru' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            RU
          </button>
        </div>

        {/* Notifications */}
        <div ref={notifRef} className="relative">
          <button
            onClick={() => { setNotifOpen((o) => !o); setAvatarOpen(false); }}
            className="relative w-9 h-9 rounded-xl bg-muted flex items-center justify-center hover:bg-secondary transition-colors"
            aria-label={language === 'fr' ? 'Notifications' : 'Уведомления'}
          >
            <Bell size={16} className="text-muted-foreground" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-danger rounded-full flex items-center justify-center text-white text-[9px] font-700">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notification panel */}
          {notifOpen && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-2xl card-shadow-lg overflow-hidden animate-fade-in">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <div>
                  <h3 className="text-sm font-700 text-foreground">
                    {language === 'fr' ? 'Notifications' : 'Уведомления'}
                  </h3>
                  {unreadCount > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {unreadCount} {language === 'fr' ? 'non lue(s)' : 'непрочитанных'}
                    </p>
                  )}
                </div>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-600 transition-colors"
                  >
                    <CheckCheck size={13} />
                    {language === 'fr' ? 'Tout lire' : 'Прочитать все'}
                  </button>
                )}
              </div>

              {/* Notification list */}
              <div className="max-h-72 overflow-y-auto divide-y divide-border">
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <Bell size={24} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      {language === 'fr' ? 'Aucune notification' : 'Нет уведомлений'}
                    </p>
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <button
                      key={notif.id}
                      onClick={() => markRead(notif.id)}
                      className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors ${
                        !notif.read ? 'bg-primary/3' : ''
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-sm flex-shrink-0 ${NOTIF_TYPE_COLORS[notif.type] ?? 'bg-muted'}`}>
                        {notif.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-700 text-foreground truncate">{notif.title}</p>
                          {!notif.read && (
                            <span className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif.message}</p>
                        <p className="text-xs text-muted-foreground/60 mt-1">{notif.time}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Avatar / User menu */}
        <div ref={avatarRef} className="relative">
          <button
            onClick={() => { setAvatarOpen((o) => !o); setNotifOpen(false); }}
            className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center text-white text-sm font-700 cursor-pointer hover:bg-primary/90 transition-colors"
            aria-label={language === 'fr' ? 'Menu utilisateur' : 'Меню пользователя'}
          >
            {user?.avatar}
          </button>

          {/* User dropdown */}
          {avatarOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-card border border-border rounded-2xl card-shadow-lg overflow-hidden animate-fade-in">
              {/* User info */}
              <div className="px-4 py-3 border-b border-border">
                <p className="text-sm font-700 text-foreground">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-600">
                    {user?.rank}
                  </span>
                  <span className="text-xs text-muted-foreground font-600">
                    {user?.xp?.toLocaleString()} XP
                  </span>
                </div>
              </div>

              {/* Menu items */}
              <div className="p-1.5">
                <button
                  onClick={() => { setAvatarOpen(false); toast.info(language === 'fr' ? 'Profil — bientôt disponible' : 'Профиль — скоро'); }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                >
                  <User size={15} />
                  {language === 'fr' ? 'Mon profil' : 'Мой профиль'}
                </button>
                <button
                  onClick={() => { setAvatarOpen(false); toast.info(language === 'fr' ? 'Paramètres — bientôt disponible' : 'Настройки — скоро'); }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                >
                  <Settings size={15} />
                  {language === 'fr' ? 'Paramètres' : 'Настройки'}
                </button>
                <div className="border-t border-border my-1" />
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-danger hover:bg-danger/10 transition-colors"
                >
                  <LogOut size={15} />
                  {language === 'fr' ? 'Déconnexion' : 'Выход'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}